import { getLocalizedMessage } from "@/shared/lib/errors/getLocalizedMessage";
import { formatBytes } from "@/shared/lib/formatBytes";

function emptyShape() {
  return {
    caseId: "",
    createdAt: "",
    lastCheckedAt: "",
    lastCheckedAtRaw: null,
    hasAiResult: false,
    needsRecheck: false,
    summary: "",
    status: "",
    verdictStatus: null,
    verdictLabel: null,
    score: {
      confidence: null,
      refusalProb: null,
      criticalCount: 0,
      warningCount: 0,
    },
    signals: [],
    documents: [],
    graphNodes: [],
    graphEdges: [],
  };
}

function mapSeverity(s) {
  if (s === "error") return "bad";
  if (s === "ok") return "ok";
  if (s === "warning") return "warn";
  return "warn";
}

function formatCheckedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString("ru-RU", { minimumFractionDigits: 2 });
}

function fieldValue(field) {
  if (!field) return "";
  if (field.type === "bool") return field.value ? "Есть" : "Нет";
  if (field.type === "amount") return formatAmount(field.value);
  return field.value == null ? "" : String(field.value);
}

function mapFieldStatus(s) {
  if (s === "error") return "bad";
  if (s === "warning") return "warn";
  return "ok";
}

function extractSections(notes, pick) {
  const sections = Array.isArray(notes?.sections) ? notes.sections : [];
  return sections
    .map((section) => {
      const fields = Array.isArray(section?.fields) ? section.fields : [];
      return {
        title: pick(section?.title) || "",
        fields: fields.map((field) => {
          const isMissingBool =
            field?.type === "bool" && field?.value === false;
          return {
            k: pick(field?.label) || "",
            v: fieldValue(field),
            ok: isMissingBool ? "bad" : mapFieldStatus(field?.status),
          };
        }),
      };
    })
    .filter((s) => s.fields.length > 0);
}

function computeDocStatus(notes) {
  const sections = Array.isArray(notes?.sections) ? notes.sections : [];
  let hasWarn = false;
  for (const section of sections) {
    const fields = Array.isArray(section?.fields) ? section.fields : [];
    for (const field of fields) {
      if (field?.status === "error") return "bad";
      if (field?.status === "warning") hasWarn = true;
    }
  }
  // Учитываем issues и notes.status — фронт получает их и без полного ai_result
  const issues = Array.isArray(notes?.issues) ? notes.issues : [];
  for (const issue of issues) {
    if (issue?.severity === "error") return "bad";
    if (issue?.severity === "warning") hasWarn = true;
  }
  if (notes?.status === "unrecognized") hasWarn = true;
  return hasWarn ? "warn" : "ok";
}

function countDocIssues(notes) {
  const sections = Array.isArray(notes?.sections) ? notes.sections : [];
  let errors = 0;
  let warnings = 0;
  for (const section of sections) {
    const fields = Array.isArray(section?.fields) ? section.fields : [];
    for (const field of fields) {
      if (field?.status === "error") errors += 1;
      else if (field?.status === "warning") warnings += 1;
    }
  }
  // Также учитываем issues из notes.issues — это полезно, когда ai_result отсутствует
  const issues = Array.isArray(notes?.issues) ? notes.issues : [];
  for (const issue of issues) {
    if (issue?.severity === "error") errors += 1;
    else if (issue?.severity === "warning") warnings += 1;
  }
  return { errors, warnings };
}

// Агрегирует "generals" из items[].notes, когда ai_result == null.
// Используется как fallback при частичной AI-обработке (распознан документ, но общая проверка пакета не выполнялась).
function buildGeneralsFromItems(items) {
  const allIssues = [];
  const verdictByStatus = new Map();
  let errorCount = 0;
  let warningCount = 0;

  for (const item of items) {
    const notes = item?.notes;
    if (!notes) continue;
    const issues = Array.isArray(notes.issues) ? notes.issues : [];
    for (const issue of issues) {
      allIssues.push(issue);
      if (issue?.severity === "error") errorCount += 1;
      else if (issue?.severity === "warning") warningCount += 1;
    }
    if (notes.verdict?.status && !verdictByStatus.has(notes.verdict.status)) {
      verdictByStatus.set(notes.verdict.status, notes.verdict.label);
    }
  }

  // Приоритет вердикта: rejected > requires_manual_review > approved
  let verdict = null;
  const priority = ["rejected", "requires_manual_review", "approved"];
  for (const status of priority) {
    if (verdictByStatus.has(status)) {
      verdict = { status, label: verdictByStatus.get(status) };
      break;
    }
  }
  if (!verdict && verdictByStatus.size > 0) {
    const [status] = [...verdictByStatus.keys()];
    verdict = { status, label: verdictByStatus.get(status) };
  }

  return {
    issues: allIssues,
    verdict,
    description: null,
    confidence: null,
    acceptance_score: null,
    error_count: errorCount,
    warning_count: warningCount,
    checked_at: null,
    recommendation: null,
  };
}

// Возвращает поля с замечаниями (error/warning) для буллетов графа.
// Если есть и ошибки, и предупреждения — резервирует слот под предупреждение,
// чтобы оно не «вытеснилось» ошибками. Пусто — компонент сам отрендерит «Замечаний нет».
function pickBullets(notes, pick, limit = 2) {
  const sections = Array.isArray(notes?.sections) ? notes.sections : [];
  const errors = [];
  const warnings = [];
  for (const section of sections) {
    const fields = Array.isArray(section?.fields) ? section.fields : [];
    for (const field of fields) {
      if (field?.status === "error") errors.push(field);
      else if (field?.status === "warning") warnings.push(field);
    }
  }
  if (!errors.length && !warnings.length) return [];

  let pickedErrors;
  let pickedWarnings;
  if (errors.length && warnings.length) {
    pickedErrors = errors.slice(0, Math.max(1, limit - 1));
    pickedWarnings = warnings.slice(0, limit - pickedErrors.length);
  } else if (errors.length) {
    pickedErrors = errors.slice(0, limit);
    pickedWarnings = [];
  } else {
    pickedErrors = [];
    pickedWarnings = warnings.slice(0, limit);
  }

  return [...pickedErrors, ...pickedWarnings].map((field) => ({
    ok: field?.status === "error" ? "bad" : "warn",
    text: pick(field?.label) || "",
  }));
}

function computeGlobalStatus(issues) {
  if (!Array.isArray(issues) || issues.length === 0) return "ok";
  const hasError = issues.some((i) => i?.severity === "error");
  if (hasError) return "bad";
  const hasWarning = issues.some((i) => i?.severity === "warning");
  if (hasWarning) return "warn";
  return "ok";
}

function computeStatusLabel(verdict, pick) {
  const status = verdict?.status;
  if (status === "rejected") return "Отказано";
  if (status === "approved") return "Готово к решению";
  if (status === "requires_manual_review") return "Требуется проверка";
  return pick(verdict?.label) || "";
}

export function apiToUiData(payload, fallbackLocale) {
  if (!payload) return emptyShape();

  const pick = (v) => getLocalizedMessage(v, fallbackLocale) || "";
  const items = Array.isArray(payload.items) ? payload.items : [];

  // Главный кейс: есть полноценный ai_result от бэка.
  // Fallback: ai_result отсутствует, но в items[].notes уже есть распознанные данные
  // (бэк положил частичные результаты до общей проверки пакета).
  const aiResult = payload.ai_result || null;
  const hasAiResult = Boolean(aiResult?.check_documents);
  const hasItemNotes = items.some((it) => it?.notes);

  if (!hasAiResult && !hasItemNotes) return emptyShape();

  const generals = hasAiResult
    ? aiResult.check_documents.generals || {}
    : buildGeneralsFromItems(items);

  // results — источник правды для документов. При отсутствии ai_result строим из items.
  const results =
    hasAiResult && Array.isArray(aiResult.check_documents.results)
      ? aiResult.check_documents.results
      : items.map((it) => ({
          document_uuid: it?.uuid,
          notes: it?.notes || {},
        }));

  const itemsByUuid = new Map();
  for (const it of items) {
    if (it?.uuid) itemsByUuid.set(it.uuid, it);
  }

  const issues = Array.isArray(generals.issues) ? generals.issues : [];
  const globalStatus = computeGlobalStatus(issues);
  const errorCount = generals.error_count ?? 0;
  const warningCount = generals.warning_count ?? 0;

  const signals = issues.map((issue, i) => ({
    id: `sig-${i}`,
    severity: mapSeverity(issue?.severity),
    title: pick(issue?.message),
  }));

  const documents = results.map((result) => {
    const docUuid = result?.document_uuid;
    const item = docUuid ? itemsByUuid.get(docUuid) : null;
    const notes = result?.notes || {};
    const docStatus = computeDocStatus(notes);
    const docIssues = countDocIssues(notes);
    return {
      id: docUuid,
      name: pick(notes.title) || item?.file_name || "",
      size: formatBytes(item?.file_size ?? 0),
      status: docStatus,
      errorsCount: docIssues.errors,
      warningsCount: docIssues.warnings,
      aiVerdict: pick(notes.description),
      aiRecommendation: pick(notes.recommendation),
      extractedSections: extractSections(notes, pick),
    };
  });

  const docStatusByUuid = new Map();
  for (const result of results) {
    if (result?.document_uuid) {
      docStatusByUuid.set(
        result.document_uuid,
        computeDocStatus(result?.notes || {})
      );
    }
  }

  const graphNodes = items.map((item) => ({
    id: item?.uuid,
    code: item?.document_type_code || null,
    name: pick(item?.notes?.title) || item?.file_name || "",
    status: docStatusByUuid.get(item?.uuid) || globalStatus,
    bullets: pickBullets(item?.notes, pick, 2),
  }));

  const graphEdges = [];
  if (graphNodes.length > 1) {
    const from = graphNodes[0].id;
    const edgeOk = globalStatus === "ok";
    for (let i = 1; i < graphNodes.length; i++) {
      graphEdges.push({ from, to: graphNodes[i].id, ok: edgeOk });
    }
  }

  const txId = aiResult?.transaction_id ?? items[0]?.transaction_id ?? null;

  // changed_at — время последнего запуска AI-проверки (приоритет).
  // checked_at — fallback для обратной совместимости.
  const lastCheckedAtRaw = hasAiResult
    ? aiResult?.changed_at ?? generals.checked_at ?? null
    : null;

  // needs_recheck — флаг от бэка: после последнего запуска AI были изменения
  // в пакете документов (загрузка/удаление). Основной источник истины для stale.
  const needsRecheck = hasAiResult && aiResult?.needs_recheck === true;

  return {
    caseId: txId != null ? String(txId) : "",
    createdAt: formatCheckedAt(generals.checked_at),
    lastCheckedAt: formatCheckedAt(lastCheckedAtRaw),
    lastCheckedAtRaw,
    hasAiResult,
    needsRecheck,
    summary: pick(generals.description),
    status: computeStatusLabel(generals.verdict, pick),
    verdictStatus: generals.verdict?.status || null,
    verdictLabel: pick(generals.verdict?.label) || null,
    score: {
      confidence: generals.confidence ?? null,
      refusalProb:
        generals.acceptance_score != null
          ? 100 - generals.acceptance_score
          : null,
      criticalCount: errorCount,
      warningCount,
    },
    signals,
    documents,
    graphNodes,
    graphEdges,
  };
}
